/**
 * Mobile Search Scroll Handler
 * Manages the mobile search bar visibility based on scroll position
 */

class MobileSearchScrollHandler {
  constructor() {
    this.scrollThreshold = 500;
    this.scrollHysteresis = 20; // Base hysteresis to prevent flicker; will be widened dynamically after first hide
    this.headerMain = document.querySelector('[data-site-header-mobile-search-bar]');
    this.searchIcon = document.querySelector('[data-mobile-search-scroll-icon]');
    this.searchField = null;
    this.lastScrollY = 0;
    this.lastUpdateScrollY = 0;
    this.ticking = false;
    this.isSearchBarHidden = false;
    this.manuallyOpened = false;
    // Cooldown prevents immediate re-show caused by layout shift when the bar collapses
    this.cooldownMs = 300;
    this.cooldownUntil = 0;
    // Track header height to compute dynamic hysteresis equal to collapse height
    this.lastHeaderHeight = 0;
  // Freeze updates briefly after a manual open to avoid immediate re-hide
  this.manualFreezeUntil = 0;
    
    if (!this.headerMain) {
      return; // Only initialize if mobile search bar is enabled
    }
    
    this.init();
  }
  
  init() {
    // Find the search field
    this.searchField = this.headerMain.querySelector('.live-search:not(.live-search--takeover)');
    
    if (!this.searchField) {
      return;
    }
    
    // Bind scroll handler
    this.handleScroll = this.handleScroll.bind(this);
    this.update = this.update.bind(this);
    
    // Add scroll event listener with passive flag for better performance
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    
    // Add click handler for search icon
    if (this.searchIcon) {
      this.searchIcon.addEventListener('click', (e) => {
        // Keep the page from navigating or bubbling into other handlers
        if (e && typeof e.preventDefault === 'function') e.preventDefault();
        if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
        this.manuallyOpened = true;
        // Expand search in place without changing scroll position
        this.showSearchBar();
        // Ensure the hidden class is removed immediately at the container level
        if (this.headerMain.classList.contains('search-bar-hidden')) {
          this.headerMain.classList.remove('search-bar-hidden');
        }
        this.isSearchBarHidden = false;
        // Prevent the next update cycle from interpreting a layout shift as a downward scroll
        this.lastUpdateScrollY = this.lastScrollY;
        const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
        this.manualFreezeUntil = now + 800;
        // Sync offsets for collection utilities so it doesn't overlap during header growth
        this.syncCollectionUtilsOffset();
        // Avoid auto-scrolling side-effects on mobile by NOT auto-focusing the input.
        // Let the user tap into the field; this preserves scroll position in all browsers.
        setTimeout(() => { this.manuallyOpened = false; }, 850);
      });
    }
    
    // Initial check
    this.lastScrollY = window.scrollY || 0;
    this.lastHeaderHeight = this.headerMain.offsetHeight || 0;
    this.update();
  }
  
  handleScroll() {
    // Clamp to avoid negative on iOS bounce
    this.lastScrollY = Math.max(0, window.scrollY || 0);
    
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
      
      this.ticking = true;
    }
  }
  
  update() {
    const now = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    // Briefly freeze logic after a manual open so layout shifts don't re-hide
    if (now < this.manualFreezeUntil) return;
    
    // Use asymmetric thresholds and direction gating to prevent flicker:
    // - Hide immediately when we cross the main threshold while scrolling down
    // - Only show again after scrolling back above a lower threshold (hysteresis) while scrolling up
    const hideThreshold = this.scrollThreshold;
    const showThreshold = this.scrollThreshold - this.scrollHysteresis;

    // Determine scroll direction with a small deadzone to ignore sub-pixel jitter
    const dz = 1; // px deadzone
    const directionDown = this.lastScrollY > this.lastUpdateScrollY + dz;
    const directionUp = this.lastScrollY < this.lastUpdateScrollY - dz;
  // 'now' already computed above

    if (!this.isSearchBarHidden) {
      // Currently visible: hide as soon as we cross the threshold while moving down
      if (this.lastScrollY >= hideThreshold && directionDown) {
        this.hideSearchBar();
        // Start cooldown so collapse-induced scroll change cannot immediately re-show it
        this.cooldownUntil = now + this.cooldownMs;
      }
    } else {
      // Currently hidden: only show if cooldown elapsed AND we scroll back above the lower threshold while moving up
      if (now >= this.cooldownUntil && this.lastScrollY <= showThreshold && directionUp) {
        this.showSearchBar();
      }
    }

    // Remember last processed scroll position
    this.lastUpdateScrollY = this.lastScrollY;
  }
  
  hideSearchBar() {
    this.headerMain.classList.add('search-bar-hidden');
    this.isSearchBarHidden = true;
    // On next frame, measure new height and widen hysteresis to at least the collapse delta + small buffer
    window.requestAnimationFrame(() => {
      const newHeight = this.headerMain.offsetHeight || 0;
      const delta = Math.max(0, this.lastHeaderHeight - newHeight);
      if (delta > 0) {
        // Ensure show threshold is below the maximum layout shift
        this.scrollHysteresis = Math.max(this.scrollHysteresis, delta + 6);
      }
      this.lastHeaderHeight = newHeight;
      // Adjust collection utilities offset after collapse to avoid overlap
      this.syncCollectionUtilsOffset();
    });
  }
  
  showSearchBar() {
    this.headerMain.classList.remove('search-bar-hidden');
    this.isSearchBarHidden = false;
    // Update header height reference after expansion settles
    window.requestAnimationFrame(() => {
      this.lastHeaderHeight = this.headerMain.offsetHeight || this.lastHeaderHeight;
      // Adjust collection utilities offset after expansion to avoid overlap
      this.syncCollectionUtilsOffset();
    });
  }

  // Ensure the collection utilities sticky bar sits just below the header, even when header height changes
  syncCollectionUtilsOffset() {
    try {
      const body = document.body;
      const stickyHeaderClass = 'site-header-sticky';
      const header = document.querySelector('[data-site-header-main]') || this.headerMain;
      if (!header) return;
      const headerHeight = header.offsetHeight || 0;

      const stickyUtils = document.querySelectorAll('[data-sticky-utils]');
      const stickyTargets = document.querySelectorAll('[data-utils-intersection-target]');

      // If sticky header is active, push the utilities bar down by header height and shift the intersection target up
      if (body.classList.contains(stickyHeaderClass)) {
        const topVal = `${headerHeight}px`;
        stickyUtils.forEach((el) => { el.style.top = topVal; });
        stickyTargets.forEach((el) => { el.style.top = `-${topVal}`; });
      } else {
        stickyUtils.forEach((el) => { el.style.top = '0'; });
        stickyTargets.forEach((el) => { el.style.top = '0'; });
      }

      // Nudge any observers relying on layout by dispatching a resize event
      window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });
    } catch (_) {
      // no-op: defensive guard for pages without collection utilities
    }
  }
  
  destroy() {
    window.removeEventListener('scroll', this.handleScroll);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new MobileSearchScrollHandler();
  });
} else {
  new MobileSearchScrollHandler();
}
